import { useMemo } from 'react';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSingleFlightMutation } from './useSingleFlightMutation';
import type {
  CreatePaymentFeeRuleV2Request,
  CreatePaymentMethodProfileRequest,
  ReviseReceiptPartFeeRequest,
  UpdatePaymentFeeRuleV2Request,
  UpdatePaymentMethodProfileRequest,
} from '@valiosascrm/shared';
import { cashFlowKeys } from '@/hooks/useCashFlow';
import * as fees from '@/services/payment-fee-config';

// Story 65.20 — taxas por máquina e bandeira e taxa das partes. Toda leitura aqui só é ligada
// por quem já sabe que o recebimento em partes está ligado na clínica (`useReceiptsEnabled`).

export const feeConfigKeys = {
  all: ['payment-fee-config'] as const,
  config: () => [...feeConfigKeys.all, 'config'] as const,
  partFees: (state: string) => [...feeConfigKeys.all, 'part-fees', state] as const,
};

export function usePaymentFeeConfig(enabled: boolean) {
  return useQuery({ queryKey: feeConfigKeys.config(), queryFn: () => fees.getPaymentFeeConfig(), enabled, retry: false });
}

function useConfigMutation<TInput, TOutput>(run: (input: TInput) => Promise<TOutput>) {
  const queryClient = useQueryClient();
  return useSingleFlightMutation({
    mutationFn: run,
    // Em erro também relê: conflito de versão significa que a tela estava desatualizada.
    onSettled: () => void queryClient.invalidateQueries({ queryKey: feeConfigKeys.config() }),
  });
}

export const useCreatePaymentMethodProfile = () =>
  useConfigMutation((request: CreatePaymentMethodProfileRequest) => fees.createPaymentMethodProfile(request));

export const useUpdatePaymentMethodProfile = () =>
  useConfigMutation(({ id, ...request }: UpdatePaymentMethodProfileRequest & { id: string }) => fees.updatePaymentMethodProfile(id, request));

export const useCreatePaymentFeeRuleV2 = () =>
  useConfigMutation((request: CreatePaymentFeeRuleV2Request) => fees.createPaymentFeeRuleV2(request));

export const useUpdatePaymentFeeRuleV2 = () =>
  useConfigMutation(({ id, ...request }: UpdatePaymentFeeRuleV2Request & { id: string }) => fees.updatePaymentFeeRuleV2(id, request));

export const useRemovePaymentFeeRuleV2 = () =>
  useConfigMutation(({ id, expectedRevision }: { id: string; expectedRevision: number }) => fees.removePaymentFeeRuleV2(id, expectedRevision));

/**
 * Story 65.22 (C6): a lista continua da última parte lida. Página cheia quer dizer que pode
 * haver mais; `data` é tudo o que já foi lido, em ordem.
 */
export function useReceiptPartFees(state: 'pending' | 'settled', enabled: boolean) {
  const query = useInfiniteQuery({
    queryKey: feeConfigKeys.partFees(state),
    queryFn: ({ pageParam }) => fees.getReceiptPartFees(state, pageParam ?? undefined),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => (last.length === fees.RECEIPT_PART_FEES_PAGE ? last[last.length - 1].partId : undefined),
    enabled,
    retry: false,
  });
  const data = useMemo(() => query.data?.pages.flat(), [query.data]);
  return {
    data, isLoading: query.isLoading, isError: query.isError, isFetching: query.isFetching, refetch: query.refetch,
    hasNextPage: query.hasNextPage, isFetchingNextPage: query.isFetchingNextPage, fetchNextPage: query.fetchNextPage,
  };
}

export function useReceiptPartFeeAges(enabled: boolean) {
  return useQuery({ queryKey: [...feeConfigKeys.all, 'part-fee-ages'] as const, queryFn: () => fees.getReceiptPartFeeAges(), enabled, retry: false });
}

export function useReviseReceiptPartFee() {
  const queryClient = useQueryClient();
  return useSingleFlightMutation({
    mutationFn: ({ rootPartId, ...request }: ReviseReceiptPartFeeRequest & { rootPartId: string }) => fees.reviseReceiptPartFee(rootPartId, request),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: feeConfigKeys.all });
      void queryClient.invalidateQueries({ queryKey: cashFlowKeys.all });
    },
  });
}
